<template>
    <DsBlock
        class="c-sticky-panel"
        :class="dynamicClass"
        :style="{
            zIndex: `${panelZIndex}`,
        }"
    >
        <div class="c-sticky-panel__content">
            <div class="c-sticky-panel__content-top">
                <StickyMedia
                    v-if="!mobileFirstBreakpoints.xl"
                    :image-alt="imageAlt"
                    :image-src="image"
                    :is-active="true"
                    :stacking-index="mediaZIndex"
                    :media-focal-point="mediaFocalPoint"
                ></StickyMedia>
                <DsTag
                    v-if="tag"
                    class="c-sticky-panel__tag"
                    :text="tag"
                ></DsTag>
                <DsText
                    class="c-sticky-panel__title"
                    html-element="h2"
                    text-type="heading-5xl"
                    text-style="medium"
                    >{{ title }}</DsText
                >
                <DsText
                    class="c-sticky-panel__text"
                    text-type="body-md"
                    text-style="regular"
                    >{{ text }}</DsText
                >
                <DsButton
                    v-if="linkText"
                    :variant="dynamicLinkVariant"
                    :href="linkUrl"
                    :icon-end="dynamicLinkIcon"
                    :target="linkIsExternal ? '_blank' : undefined"
                    >{{ linkText }}</DsButton
                >
            </div>
            <div v-if="facts?.length" class="c-sticky-panel__content-bottom">
                <FactWithIcon
                    v-for="fact in facts"
                    :key="fact.text"
                    :stat="fact.stat"
                    :brand-icon-name="fact.brandIconName"
                    :variant="dynamicFactVariant"
                    :text="fact.text"
                ></FactWithIcon>
            </div>
        </div>
    </DsBlock>
</template>

<script setup lang="ts">
import { computed, type ComputedRef } from "vue";
import { DsBlock, DsButton, DsTag, DsText, dsUseBreakpoints } from "@coloplast/design-system";
import type { ObjectPositionOption } from "~/@coloplast/design-system/dist/types/types/object-position";
import type { IconName } from "~/@coloplast/design-system/dist/types/components/icon/interface";
import FactWithIcon, { type FactProps } from "../fact-with-icon/fact-with-icon.vue";
import StickyMedia from "./sticky-media.vue";

export type StickyPanel = {
    activeBgColour?: string;
    activeIndex?: number;
    bgColour?: "inverse" | "decorative-2-subtle" | "decorative-3-muted";
    facts?: Array<FactProps>;
    index?: number;
    linkIsExternal?: boolean;
    linkText?: string;
    linkUrl?: string;
    numberOfPanels?: number;
    mediaFocalPoint?: ObjectPositionOption;
    tag?: string;
    title: string;
    text: string;
    image: string;
    imageAlt?: string;
};

const props = withDefaults(defineProps<StickyPanel>(), {
    linkIsExternal: false,
    mediaFocalPoint: "center",
    numberOfPanels: 0,
    index: 0,
});

const mobileFirstBreakpoints = dsUseBreakpoints(["xl"]);

const externalIconName: IconName = "button-external-link";
const arrowIconName: IconName = "button-navigate-forward";

const dynamicLinkIcon: ComputedRef<Partial<IconName>> = computed(() => {
    return props.linkIsExternal ? externalIconName : arrowIconName;
});

const dynamicFactVariant = computed(() => {
    if (!mobileFirstBreakpoints.xl)
        return props.bgColour === "inverse" ? "inverse" : "default";
    return props.activeBgColour === "inverse" ? "inverse" : "default";
});

const dynamicLinkVariant = computed(() => {
    if (!mobileFirstBreakpoints.xl)
        return props.bgColour === "inverse" ? "link-inverted" : "link";
    return props.activeBgColour === "inverse" ? "link-inverted" : "link";
});

const dynamicClass = computed(() => {
    const panelClasses = [];
    if (props.activeIndex === props.index) {
        panelClasses.push("c-sticky-panel--active");
    }
    panelClasses.push(`c-sticky-panel--bg-${props.bgColour}`);
    return panelClasses;
});

const panelZIndex = computed(() => {
    return props.index;
});

const mediaZIndex = computed(() => {
    return props.numberOfPanels - props.index;
});
</script>
